import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, ShieldCheck, Send } from "lucide-react";
import PageHead from "@/components/PageHead";
import BetaNotice from "@/components/BetaNotice";

export default function Landing() {
  return (
    <>
      <PageHead
        title="ChronoVaults | Secure Future Messaging"
        description="Create secure video and personal messages today and schedule them for delivery at meaningful moments in the future."
        canonical="https://chronovaults.co.uk/"
      />
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 animate-fade-in">
              <p className="label-mono text-terracotta">EST. 2026 · TIME-LOCKED VIDEO VAULT</p>
              <BetaNotice variant="pill" />
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-bone mt-8">
                Record now.<br />
                Deliver <em className="text-terracotta not-italic font-light">precisely</em><br />
                when it matters.
              </h1>
              <p className="mt-8 max-w-xl text-base text-white/60 leading-relaxed">
                ChronoVaults lets you seal a video for the people in your contact list — birthdays, milestones, a promise
                you kept for a decade. It stays locked until the event you set, then delivers itself.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/register" data-testid="hero-cta-primary">
                  <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-8 py-6 text-sm tracking-wide">
                    Seal your first capsule <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-cta-secondary">
                  <Button variant="ghost" className="text-bone/80 hover:text-bone rounded-full px-6">
                    I have an account
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-white/10">
                <img
                  src="https://images.pexels.com/photos/19920920/pexels-photo-19920920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Safety deposit boxes"
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-vault-950 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="label-mono text-bone/80">Vault No. 04 · 2029</p>
                  <p className="font-serif text-2xl text-bone mt-2">"For Amelia, on her 18th."</p>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-vault-800 border border-white/10 p-5 w-56">
                <p className="label-mono">Next release</p>
                <p className="font-serif text-3xl text-terracotta mt-2">03d 14h</p>
                <p className="text-xs text-white/50 mt-1">4 recipients notified</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-white/5 bg-vault-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-3 gap-px bg-white/5">
            {[
              { icon: Send, title: "Seal", body: "Upload a video, name your recipient, and set the release event.", num: "01" },
              { icon: Clock, title: "Wait", body: "Your capsule stays encrypted and dormant until the moment arrives.", num: "02" },
              { icon: ShieldCheck, title: "Verify & View", body: "Recipient verifies via matched phone numbers, then unseals the memory.", num: "03" },
            ].map((s) => (
              <div key={s.title} className="bg-vault-950 p-10 md:p-14">
                <p className="font-serif text-6xl text-terracotta/30 mb-8">{s.num}</p>
                <s.icon className="w-5 h-5 text-terracotta mb-6" strokeWidth={1.5} />
                <h3 className="font-serif text-3xl text-bone mb-3">{s.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-2 gap-12 items-center">
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-bone">
            Some words<br />
            <em className="text-terracotta not-italic">wait better</em> than others.
          </h2>
          <div>
            <p className="text-white/60 leading-relaxed mb-8">
              Whether it's a decade from today or the day someone turns thirty, your video is held in a private vault —
              accessible only after the moment arrives and the recipient proves who they are.
            </p>
            <Link to="/register" data-testid="footer-cta">
              <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-8 py-6">
                Create a free vault
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-white/40" data-testid="landing-footer">
        <div className="flex items-center justify-center gap-3">
          <span>ChronoVaults · A time-locked video capsule service</span>
          <span className="text-white/20">·</span>
          <Link to="/about" className="text-white/60 hover:text-bone transition-colors" data-testid="footer-about-link">
            About
          </Link>
        </div>
      </footer>
    </div>
    </>
  );
}
